import { forwardRef, HtmlHTMLAttributes, memo, useEffect, useMemo, useState } from "react";
import { number } from "card-validator";
import classNames from "classnames";
import { defaultCardType, defaultGaps } from "../inputs";
import { PayPassIcon } from "./icons";
import { formattingCardNumber, getPaymentMethodIconByCardType } from "./utils";

import styles from "./style.module.scss";

const defaultCardMinLength = 16;

enum CardNumberEnum {
  NUMBER_CARD_DEFAULT = "numberCardDefault",
  NUMBER_CARD_LESS16 = "numberCardLess16",
  NUMBER_CARD_EQUAL17 = "numberCardEqual17",
  NUMBER_CARD_EQUAL18 = "numberCardEqual18",
  NUMBER_CARD_EQUAL19 = "numberCardEqual19",
  NUMBER_CARD_LAST4 = "numberCardLast4",
}

export type CreditCardThemeType =
  "transparent"
  | "transparentGradient"
  | "brandDark"
  | "brandLight"
  | "grayDark"
  | "grayLight"
  | "transparentStrip"
  | "grayStrip"
  | "gradientStrip"
  | "salmonStrip"
  | "grayStripVertical"
  | "gradientStripVertical"
  | "salmonStripVertical"

export type CreditCardProps = {
  text?: string
  theme?: CreditCardThemeType
  cardName?: string
  cardBrand?: string | null
  cardNumber?: string
  textClassName?: string
  cardNumberLast4?: string | null
  cardDateValidity?: string
  cardDateClassName?: string
  cardNameClassName?: string
  cardNumberClassName?: string
  paymentBlockClassName?: string
  payPassBlockClassName?: string
} & HtmlHTMLAttributes<HTMLDivElement>

export const CreditCard = memo(forwardRef<HTMLDivElement, CreditCardProps>((
  {
    text,
    theme = "transparent",
    cardName = "cardholder",
    className,
    cardBrand = null,
    cardNumber = "",
    textClassName,
    cardNumberLast4 = null,
    cardDateValidity,
    cardDateClassName,
    cardNameClassName,
    cardNumberClassName,
    paymentBlockClassName,
    payPassBlockClassName,
    ...props
  }, ref) => {
  const [ cardType, setCardType ] = useState(defaultCardType);
  const [ gaps, setGaps ] = useState(defaultGaps);

  useEffect(() => {
    const cardNumberVerification = number(cardNumber);
    if (cardNumberVerification.isPotentiallyValid) {
      setCardType(cardNumberVerification.card?.type || defaultCardType);
      setGaps(cardNumberVerification?.card?.gaps || defaultGaps);
    } else {
      setCardType(defaultCardType);
      setGaps(defaultGaps);
    }
  }, [ cardNumber ])

  const determineCardNumberFormat = useMemo(() : CardNumberEnum => {
    if(cardNumberLast4) return CardNumberEnum.NUMBER_CARD_LAST4

    if (cardNumber === "") {
      return CardNumberEnum.NUMBER_CARD_DEFAULT;
    } else if (cardNumber.length <= 16) {
      return CardNumberEnum.NUMBER_CARD_LESS16;
    } else if (cardNumber.length === 17) {
      return CardNumberEnum.NUMBER_CARD_EQUAL17;
    } else if (cardNumber.length === 18) {
      return CardNumberEnum.NUMBER_CARD_EQUAL18;
    } else {
      return CardNumberEnum.NUMBER_CARD_EQUAL19;
    }
  }, [ cardNumber, cardNumberLast4 ])

  return (
    <div
      className={classNames(
        className,
        styles.root,
        styles[theme as keyof typeof styles]
      )}
      {...props}
      ref={ref}
    >
      <span className={classNames(textClassName, styles.text)}>{text}</span>
      <PayPassIcon className={classNames(payPassBlockClassName, styles.payPassBlock)} />
      <span className={classNames(cardNameClassName, styles.nameCard)}>{cardName}</span>
      <span className={classNames(cardDateClassName, styles.dateCard)}>{cardDateValidity}</span>
      <span
        className={classNames(cardNumberClassName, styles.numberCard, {
          [styles.numberCardLast4]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_LAST4,
          [styles.numberCardDefault]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_DEFAULT,
          [styles.numberCardLess16]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_LESS16,
          [styles.numberCardEqual17]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_EQUAL17,
          [styles.numberCardEqual18]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_EQUAL18,
          [styles.numberCardEqual19]: determineCardNumberFormat === CardNumberEnum.NUMBER_CARD_EQUAL19,
        })}
      >
        {cardNumberLast4
          ? formattingCardNumber("•".repeat(defaultCardMinLength - 4) + cardNumberLast4, gaps)
          : cardNumber
            ? formattingCardNumber(cardNumber, gaps)
            : formattingCardNumber("•".repeat(defaultCardMinLength), gaps)
        }
      </span>
      <span className={classNames(paymentBlockClassName, styles.paymentBlock)}>
        {getPaymentMethodIconByCardType(cardBrand || cardType)}
      </span>
    </div>
  );
}));